import React, { useRef, useEffect, useState } from 'react';

export default function LiquidMaskReveal({
  baseImage,
  revealImage,
  alt = 'Maycon Robert',
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Loaded images state
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const imgBaseRef = useRef(null);
  const imgRevealRef = useRef(null);

  // Mouse tracking state
  const mouseRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0 });
  const isHoveredRef = useRef(false);
  const isTouchingRef = useRef(false);

  // Physics chain for continuous liquid trail
  const trailPointsRef = useRef([]);
  const trailLength = 22; // Very long, majestic trailing tail

  // Smooth scale ref to grow/shrink mask on hover
  const blobScaleRef = useRef(0);

  // Reusable offscreen canvases for locked 60fps performance
  const maskCanvasTRef = useRef(null); // RAW gradient canvas
  const maskCanvasRef = useRef(null);  // Custom CPU thresholded gooey mask
  const revealCanvasRef = useRef(null);

  // Dimensions state
  const dimensionsRef = useRef({ width: 0, height: 0 });

  // 1. Load Images
  useEffect(() => {
    let loadedCount = 0;
    const checkLoaded = () => {
      loadedCount++;
      if (loadedCount === 2) {
        setImagesLoaded(true);
      }
    };

    const imgBase = new Image();
    imgBase.src = baseImage;
    imgBase.onload = checkLoaded;
    imgBaseRef.current = imgBase;

    const imgReveal = new Image();
    imgReveal.src = revealImage;
    imgReveal.onload = checkLoaded;
    imgRevealRef.current = imgReveal;

    return () => {
      imgBase.onload = null;
      imgReveal.onload = null;
    };
  }, [baseImage, revealImage]);

  // 2. Setup Canvas and Animation Loop
  useEffect(() => {
    if (!imagesLoaded) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const handleResize = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      const w = rect.width * dpr;
      const h = rect.height * dpr;

      // Primary visible canvas
      canvas.width = w;
      canvas.height = h;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      // Optimized mask canvas (640px width/height: CPU-lightweight to guarantee 60/80fps in software mode, while maintaining perfect smoothness and zero banding)
      const maxMaskDim = 640;
      let maskW = w;
      let maskH = h;
      let scaleDown = 1;

      if (maskW > maxMaskDim || maskH > maxMaskDim) {
        if (maskW > maskH) {
          scaleDown = maskW / maxMaskDim;
          maskW = maxMaskDim;
          maskH = Math.ceil(h / scaleDown);
        } else {
          scaleDown = maskH / maxMaskDim;
          maskH = maxMaskDim;
          maskW = Math.ceil(w / scaleDown);
        }
      }

      if (!maskCanvasTRef.current) maskCanvasTRef.current = document.createElement('canvas');
      maskCanvasTRef.current.width = maskW;
      maskCanvasTRef.current.height = maskH;

      if (!maskCanvasRef.current) maskCanvasRef.current = document.createElement('canvas');
      maskCanvasRef.current.width = maskW;
      maskCanvasRef.current.height = maskH;

      // Offscreen canvas: Clipped Reveal B (High resolution for detailed textures)
      if (!revealCanvasRef.current) revealCanvasRef.current = document.createElement('canvas');
      revealCanvasRef.current.width = w;
      revealCanvasRef.current.height = h;

      dimensionsRef.current = {
        width: w,
        height: h,
        maskWidth: maskW,
        maskHeight: maskH,
        scaleDown: scaleDown,
      };

      const cx = w / 2;
      const cy = h / 2;

      // Initialize physics points at center if not initialized
      if (trailPointsRef.current.length === 0) {
        const points = [];
        for (let i = 0; i < trailLength; i++) {
          points.push({
            x: cx,
            y: cy,
            vx: 0,
            vy: 0,
          });
        }
        trailPointsRef.current = points;

        mouseRef.current.x = cx;
        mouseRef.current.y = cy;
        mouseRef.current.targetX = cx;
        mouseRef.current.targetY = cy;
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    let animationFrameId;

    // Helper: Draw image with object-fit: cover logic
    const drawCoverImage = (targetCtx, img, w, h) => {
      const imgW = img.width;
      const imgH = img.height;
      const r = imgW / imgH;
      const cr = w / h;
      let nw = w;
      let nh = h;
      let cx = 0;
      let cy = 0;

      if (cr > r) {
        nh = w / r;
      } else {
        nw = h * r;
      }

      cx = (w - nw) * 0.5;
      cy = (h - nh) * 0.5;

      targetCtx.drawImage(img, cx, cy, nw, nh);
    };

    let lastTime = performance.now();

    // Render loop
    const render = () => {
      const d = dimensionsRef.current;
      if (d.width === 0 || d.height === 0) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      const now = performance.now();
      // Normalize dt so that at 60 FPS (16.67ms per frame), dt is exactly 1.0.
      let dt = (now - lastTime) / 16.666;
      lastTime = now;

      // Limit dt to avoid huge lag jumps when tab is suspended or loaded
      if (dt > 4.0) dt = 4.0;

      const mouse = mouseRef.current;
      const activeInteraction = isHoveredRef.current || isTouchingRef.current;

      // 1. Lerp mask active scale (Time-independent exponential decay)
      const targetScale = activeInteraction ? 1.0 : 0.0;
      const scaleFactor = 1 - Math.exp(-0.083 * dt);
      blobScaleRef.current += (targetScale - blobScaleRef.current) * scaleFactor;

      // 2. Smoothly interpolate cursor position (lerp) - Highly responsive and fluid
      const lerpCoeff = activeInteraction ? 0.30 : 0.40;
      const cursorFactor = 1 - Math.exp(-lerpCoeff * dt);
      mouse.x += (mouse.targetX - mouse.x) * cursorFactor;
      mouse.y += (mouse.targetY - mouse.y) * cursorFactor;

      const time = Date.now() * 0.012;

      // 3. Update spring-damper physics trail (pure and stable physics simulation)
      const trail = trailPointsRef.current;
      if (trail.length > 0) {
        // Head point snaps/lerps to the cursor
        trail[0].x = mouse.x;
        trail[0].y = mouse.y;

        // Propagate down the chain with highly stable spring-damper physics
        for (let i = 1; i < trail.length; i++) {
          const p = trail[i];
          const target = trail[i - 1];

          const dx = target.x - p.x;
          const dy = target.y - p.y;

          // Highly fluid, viscous spring constant and damping for organic liquid lagging
          const k = 0.28; 
          const damping = 0.58; 

          // Scaled physics updates using Delta Time
          p.vx += dx * k * dt;
          p.vy += dy * k * dt;
          p.vx *= Math.pow(damping, dt);
          p.vy *= Math.pow(damping, dt);

          p.x += p.vx * dt;
          p.y += p.vy * dt;
        }
      }

      // 4. Render Raw Mask with Overlapping Radial Gradients on Solid Black BG
      const maskCanvasT = maskCanvasTRef.current;
      const maskCtxT = maskCanvasT.getContext('2d');
      maskCtxT.fillStyle = '#000000';
      maskCtxT.fillRect(0, 0, d.maskWidth, d.maskHeight);

      if (blobScaleRef.current > 0.001 && trail.length > 0) {
        const baseRadius = d.width < 600 ? d.width * 0.22 : d.width * 0.12;

        trail.forEach((p, i) => {
          const t = i / trail.length;
          // Taper radius with exact 1.3 power factor
          const sizeMultiplier = Math.pow(1 - t, 1.3);

          // Apply organic wave-like fluid displacement and wobble oscillation
          const waveX = Math.sin(time - i * 0.5) * 4 * blobScaleRef.current;
          const waveY = Math.cos(time - i * 0.5) * 3 * blobScaleRef.current;
          
          const wobble = 6 * Math.sin(time - 0.6 * i) * blobScaleRef.current;
          const r = ((baseRadius * sizeMultiplier + wobble) * blobScaleRef.current) / d.scaleDown;

          if (r > 0.1) {
            const px = (p.x + waveX) / d.scaleDown;
            const py = (p.y + waveY) / d.scaleDown;

            const gradient = maskCtxT.createRadialGradient(px, py, 0, px, py, r);
            gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
            gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.85)');
            gradient.addColorStop(0.65, 'rgba(255, 255, 255, 0.25)');
            gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

            maskCtxT.fillStyle = gradient;
            maskCtxT.beginPath();
            maskCtxT.arc(px, py, r, 0, Math.PI * 2);
            maskCtxT.fill();
          }
        });
      }

      // 5. Apply CPU-Based Gooey & Smooth Anti-Aliasing Thresholding
      const maskCanvas = maskCanvasRef.current;
      const maskCtx = maskCanvas.getContext('2d');
      maskCtx.clearRect(0, 0, d.maskWidth, d.maskHeight);
      maskCtx.drawImage(maskCanvasT, 0, 0);

      const imgData = maskCtx.getImageData(0, 0, d.maskWidth, d.maskHeight);
      const pixels = imgData.data;
      const threshold = 120; // Perfect gooey metaball fusion threshold

      for (let e = 0; e < pixels.length; e += 4) {
        const red = pixels[e]; // Read raw gradient brightness (Red channel)
        
        if (red > threshold) {
          pixels[e + 3] = 255; // Solid opaque inside the fluid core
        } else {
          // Ultra-smooth border anti-aliasing / soft feathered falloff
          const softFalloff = red / threshold;
          pixels[e + 3] = Math.max(0, Math.floor(255 * softFalloff));
        }
        
        // FORCE all RGB color channels to solid white to make banding physically impossible
        pixels[e] = 255;     // R
        pixels[e + 1] = 255; // G
        pixels[e + 2] = 255; // B
      }
      maskCtx.putImageData(imgData, 0, 0);

      // 6. Draw Base Image A on Visible Canvas
      ctx.clearRect(0, 0, d.width, d.height);
      drawCoverImage(ctx, imgBaseRef.current, d.width, d.height);

      // 7. Compose Mask and Image B
      const revealCanvas = revealCanvasRef.current;
      const revealCtx = revealCanvas.getContext('2d');
      revealCtx.globalCompositeOperation = 'source-over'; // Reset persistent state before drawing to prevent transparency leakage
      revealCtx.clearRect(0, 0, d.width, d.height);
      drawCoverImage(revealCtx, imgRevealRef.current, d.width, d.height);

      // Apply thresholded trail mask using source composition (stretching the low-res mask Canvas)
      revealCtx.globalCompositeOperation = 'destination-in';
      revealCtx.drawImage(maskCanvas, 0, 0, d.width, d.height);

      // Draw the final reveal composition over the background
      ctx.drawImage(revealCanvas, 0, 0);

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [imagesLoaded]);

  // Touch & Mouse Event Handlers
  const handleMouseMove = (e) => {
    if (!containerRef.current || isTouchingRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    const tx = (e.clientX - rect.left) * dpr;
    const ty = (e.clientY - rect.top) * dpr;

    // Reset physics chain instantly on entry to prevent long-distance elastic slides
    if (!isHoveredRef.current) {
      isHoveredRef.current = true;
      const trail = trailPointsRef.current;
      if (trail.length > 0) {
        trail.forEach((p) => {
          p.x = tx;
          p.y = ty;
          p.vx = 0;
          p.vy = 0;
        });
      }
      mouseRef.current.x = tx;
      mouseRef.current.y = ty;
    }

    mouseRef.current.targetX = tx;
    mouseRef.current.targetY = ty;
  };

  const handleMouseEnter = (e) => {
    isHoveredRef.current = true;
    if (e && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const tx = (e.clientX - rect.left) * dpr;
      const ty = (e.clientY - rect.top) * dpr;

      const trail = trailPointsRef.current;
      if (trail.length > 0) {
        trail.forEach((p) => {
          p.x = tx;
          p.y = ty;
          p.vx = 0;
          p.vy = 0;
        });
      }
      mouseRef.current.x = tx;
      mouseRef.current.y = ty;
      mouseRef.current.targetX = tx;
      mouseRef.current.targetY = ty;
    }
  };

  const handleMouseLeave = () => {
    isHoveredRef.current = false;
  };

  const handleTouchStart = (e) => {
    isTouchingRef.current = true;
    if (e.touches.length > 0 && containerRef.current) {
      const touch = e.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const tx = (touch.clientX - rect.left) * dpr;
      const ty = (touch.clientY - rect.top) * dpr;

      const trail = trailPointsRef.current;
      if (trail.length > 0) {
        trail.forEach((p) => {
          p.x = tx;
          p.y = ty;
          p.vx = 0;
          p.vy = 0;
        });
      }
      mouseRef.current.x = tx;
      mouseRef.current.y = ty;
      mouseRef.current.targetX = tx;
      mouseRef.current.targetY = ty;
    }
  };

  const handleTouchMove = (e) => {
    if (!containerRef.current || e.touches.length === 0) return;
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    mouseRef.current.targetX = (touch.clientX - rect.left) * dpr;
    mouseRef.current.targetY = (touch.clientY - rect.top) * dpr;
  };

  const handleTouchEnd = () => {
    isTouchingRef.current = false;
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden select-none flex items-center justify-center"
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'none' }}
    >
      {!imagesLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-[var(--bg)] z-50">
          <div className="w-10 h-10 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="w-full h-full pointer-events-none block z-10"
      />
    </div>
  );
}
